import type { ProcessingResult } from '@/types/document-types';
import { latestHumanReviews, type HumanIdentityReview } from '@/types/identity-review-types';

export type VerificationOutcome = 'running' | 'complete' | 'incomplete' | 'failed' | 'unknown';

interface EntityScreeningEvidence {
  name?: string;
  reviewStatus?: string;
  complianceStatus?: string;
  variations?: EntityScreeningEvidence[];
}

// Consolidated records retain source screening statuses on their variations.
export function hasPendingEntityScreening(entity: EntityScreeningEvidence): boolean {
  return entity.reviewStatus === 'pending' || entity.complianceStatus === 'pending'
    || (Array.isArray(entity.variations) && entity.variations.some(hasPendingEntityScreening));
}

interface OutcomeState {
  isProcessing: boolean;
  results: ProcessingResult[];
  documentMetadata: { processingStage?: string; processingErrors?: string[] };
  consolidationResult?: { consolidatedEntities?: EntityScreeningEvidence[]; humanIdentityReviews?: HumanIdentityReview[] };
  extractedEntities?: EntityScreeningEvidence[];
}

/** Parties and reviewer-confirmed names with a potential sanctions match, one entry per name. */
export function potentialMatchNames({ results, consolidationResult, extractedEntities }: Omit<OutcomeState, 'isProcessing' | 'documentMetadata'>): string[] {
  const consolidated = consolidationResult?.consolidatedEntities ?? [];
  const entities: EntityScreeningEvidence[] = consolidated.length ? consolidated : extractedEntities?.length ? extractedEntities : results.flatMap(result => result.entities ?? []);
  const reviews = latestHumanReviews([...(consolidationResult?.humanIdentityReviews ?? []), ...results.flatMap(result => result.humanIdentityReviews ?? [])]);
  return [...new Set([
    ...entities.filter(entity => (entity.reviewStatus ?? entity.complianceStatus) === 'flagged').map(entity => entity.name?.trim() || 'Unnamed party'),
    ...reviews.filter(review => review.screening.status === 'flagged' || !!review.retainedScreening).map(review => review.confirmedName),
  ])];
}

/** Unread occurrences that have not subsequently been confirmed by a reviewer. */
export function unreadNameCount({ results, consolidationResult }: Pick<OutcomeState, 'results' | 'consolidationResult'>): number {
  const reviews = latestHumanReviews([
    ...(consolidationResult?.humanIdentityReviews ?? []),
    ...results.flatMap(result => result.humanIdentityReviews ?? []),
  ]);
  const confirmed = new Set(reviews.map(review => JSON.stringify([review.documentId, review.sourceIdentity.occurrenceId])));
  return new Set(results.flatMap(result => (result.unresolvedVisualIdentities ?? [])
    .map(identity => JSON.stringify([result.documentId, identity.occurrenceId])))
    .filter(key => !confirmed.has(key))).size;
}

export function selectVerificationOutcome({ isProcessing, results, documentMetadata, consolidationResult, extractedEntities }: OutcomeState): VerificationOutcome {
  const stage = documentMetadata.processingStage;
  // A terminal stage wins even during the final render which clears the busy flag.
  if (stage !== 'complete' && stage !== 'error' && isProcessing) return 'running';
  const failed = results.filter(result => result.status === 'failed').length;
  const reviews = latestHumanReviews([...(consolidationResult?.humanIdentityReviews ?? []), ...results.flatMap(result => result.humanIdentityReviews ?? [])]);
  if (stage === 'error' && (!results.length || failed === results.length)) return 'failed';
  if (results.length && failed === results.length) return 'failed';
  if (stage === 'error' || documentMetadata.processingErrors?.length || results.some(result =>
    result.status === 'failed'
    // Review required for a reason other than unread names (for example, consolidation).
    || (result.status === 'review_required' && (!!result.error || !result.unresolvedVisualIdentities?.length))
    || result.entities?.some(hasPendingEntityScreening)
  ) || reviews.some(review => review.screening.status === 'pending')
    || consolidationResult?.consolidatedEntities?.some(hasPendingEntityScreening)
    || extractedEntities?.some(hasPendingEntityScreening)) return 'incomplete';
  if (results.some(result => result.status === 'unknown' || result.status === 'processing')) return 'unknown';
  // Matches are a finding of a complete verification, and names nobody could
  // read are a side note; neither changes whether verification finished.
  return stage === 'complete' ? 'complete' : 'unknown';
}

export const verificationOutcomeLabel: Record<VerificationOutcome, string> = {
  running: 'Verification in progress', complete: 'Verification complete',
  incomplete: 'Verification incomplete', failed: 'Verification failed', unknown: 'Outcome unavailable'
};
